import { useAuth } from '@app/contexts/auth';
import { SnackbarLocationState, useSnackbar } from '@app/contexts/snackbar';
import { email as emailSchema } from '@app/forms/validation-schemas/email';
import { LoginContacts, LoginStep, SendTo } from '@app/pages/auth/Login/Login';
import { UserRoles } from '@app/pages/users/types';
import { getErrorMessage } from '@app/utils/http/getErrorMessage';
import { authService } from '@app/utils/services/authService';
import { credentialsService } from '@app/utils/services/credentialsService';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useHistory, useLocation } from 'react-router';
import { object, string } from 'yup';

interface FormFields {
  email: string;
  password: string;
}

export interface State {
  backLocation: Location;
  backlink: string;
}

export const useStep1 = (
  setStep: Dispatch<SetStateAction<LoginStep>>,
  setContacts: Dispatch<SetStateAction<LoginContacts>>,
  setSendTo: Dispatch<SetStateAction<SendTo>>
) => {
  const history = useHistory();
  const { state } = useLocation<SnackbarLocationState & State>();
  const { setIsAuthorized } = useAuth();
  const { open } = useSnackbar();
  const { mutate, isLoading } = useMutation(authService.login, {
    onSuccess: ({ token, expiredAt, phone, roleId }) => {
      if (token) {
        credentialsService.set({ token, expiredAt, roleId });
        setIsAuthorized(true);
        const pathname =
          roleId === UserRoles.Receiver ? '/receiver-terminal' : state?.backlink ? state?.backlink : '/dashboard';
        const { backLocation } = state || { backLocation: undefined };
        history.push({ pathname, state: { backLocation } });
      } else {
        setContacts((contacts) => ({ ...contacts, phone }));
        setSendTo(phone ? 'phone' : 'email');
        setStep(2);
      }
    },
    onError: (error: AxiosError) => {
      open({ severity: 'error', message: getErrorMessage(error) });
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors: formErrors },
  } = useForm<FormFields>({
    resolver: yupResolver(
      object().shape({
        email: emailSchema,
        password: string().required().label('Password'),
      })
    ),
    defaultValues: { email: '', password: '' },
  });

  useEffect(() => {
    if (state?.snackbar) {
      open(state.snackbar);
    }
  }, [state]);

  const onSubmit = ({ email, password }: FormFields) => {
    setContacts((contacts) => ({ ...contacts, email }));
    const timeOffset = -new Date().getTimezoneOffset();
    mutate({ email, password, timeOffset });
  };

  const onFormSubmit = handleSubmit(onSubmit);

  return {
    isLoading,
    onFormSubmit,
    control,
    formErrors,
  };
};
